import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, MessageSquare, Copy, Share2, Facebook, Send, Instagram, Mail, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, withSource } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";

const DEFAULT = "Hey! I've made my bucket list — the countries I've been to, where I want to go next and my life goals. Take a look and make your own, then we can plan trips together: {link}";

// Saved messages keep "{link}" where the link goes; if it's missing the link is added at the end
const fill = (tpl, link) => (tpl.includes("{link}") ? tpl.split("{link}").join(link) : `${tpl.trim()} ${link}`);
export const inviteText = (link, tpl) => fill(tpl || DEFAULT, link);
export const useInviteText = () => {
  const { data } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  return (link) => inviteText(link, data?.invite_text);
};

const Btn = ({ icon: Icon, label, onClick, href, testId, color }) => {
  const cls = "flex-1 min-w-[96px] flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-xl text-sm font-medium hover:-translate-y-0.5 transition-[transform,opacity] hover:opacity-90";
  const style = { background: `${color}22`, color, border: `1px solid ${color}55` };
  return href ? (
    <a href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})} className={cls} style={style} data-testid={testId}><Icon size={15} className="shrink-0" /> {label}</a>
  ) : (
    <button type="button" onClick={onClick} className={cls} style={style} data-testid={testId}><Icon size={15} className="shrink-0" /> {label}</button>
  );
};

const SaveNote = ({ text, link, onReset }) => {
  const qc = useQueryClient();
  const saved = qc.getQueryData(["settings"])?.invite_text || "";
  const put = async (invite_text, msg) => {
    try {
      const s = await api.saveSettings({ invite_text });
      qc.setQueryData(["settings"], s);
      toast.success(msg);
    } catch { toast.error("Couldn't save your message"); }
  };
  const changed = text.trim() && text !== inviteText(link, saved);
  return (
    <div className="flex items-center justify-end gap-3 text-xs -mt-1" data-testid="invite-note-actions">
      {saved && <button type="button" onClick={() => { onReset(inviteText(link)); put("", "Back to the standard message"); }} className="flex items-center gap-1 text-slate-500 hover:text-slate-300" data-testid="invite-note-reset"><RotateCcw size={12} /> Use standard message</button>}
      {changed ? (
        <button type="button" onClick={() => put(text.includes(link) ? text.split(link).join("{link}") : text, "Saved — this message is ready every time you share")}
          className="flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-400 text-[#06121c] font-medium hover:bg-emerald-300 transition-colors" data-testid="invite-note-save"><Save size={12} /> Save as my message</button>
      ) : saved && <span className="text-emerald-300/80" data-testid="invite-note-saved">Your saved message</span>}
    </div>
  );
};

export const InviteMessage = ({ link, codes = {} }) => {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  return settings ? <InviteBody link={link} codes={codes} saved={settings.invite_text} /> : null;
};

const InviteBody = ({ link, codes, saved }) => {
  const [text, setText] = useState(() => inviteText(link, saved));
  // Each channel gets its own tracked link so opens can be counted per channel
  const full = text.includes(link) ? text : `${text.trim()} ${link}`;
  const tagged = (ch) => full.split(link).join(withSource(link, codes[ch]));
  const enc = (ch) => encodeURIComponent(tagged(ch));
  const copy = async () => {
    await navigator.clipboard.writeText(tagged("copy")).catch(() => null);
    toast.success("Message copied");
  };
  // Messenger app takes a link on phones; on computers we copy the message and open messenger.com
  const messenger = async () => {
    const url = withSource(link, codes.messenger);
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = `fb-messenger://share/?link=${encodeURIComponent(url)}`;
      return;
    }
    await navigator.clipboard.writeText(tagged("messenger")).catch(() => null);
    toast.success("Message copied — paste it into a Messenger chat");
    window.open("https://www.messenger.com/", "_blank", "noopener");
  };
  // Instagram can't prefill messages: copy first, then open the chats
  const instagram = async () => {
    await navigator.clipboard.writeText(tagged("instagram")).catch(() => null);
    toast.success("Message copied — open a chat in Instagram and paste it");
    const web = "https://www.instagram.com/direct/inbox/";
    if (!/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) return void window.open(web, "_blank", "noopener");
    const t = setTimeout(() => { if (!document.hidden) window.location.href = web; }, 1500);
    document.addEventListener("visibilitychange", () => clearTimeout(t), { once: true });
    window.location.href = "instagram://direct-inbox";
  };
  const share = () => navigator.share({ text: tagged("native") }).catch(() => null);
  return (
    <div className="space-y-3" data-testid="invite-message">
      <p className="eyebrow !text-slate-500">Invite friends</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000} className="text-sm resize-none" data-testid="invite-message-text" />
      <SaveNote text={text} link={link} onReset={setText} />
      <div className="flex flex-wrap gap-2">
        <Btn icon={MessageCircle} label="WhatsApp" href={`https://wa.me/?text=${enc("whatsapp")}`} testId="invite-whatsapp-button" color="#25D366" />
        <Btn icon={Facebook} label="Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withSource(link, codes.facebook))}`} testId="invite-facebook-button" color="#60A5FA" />
        <Btn icon={Send} label="Messenger" onClick={messenger} testId="invite-messenger-button" color="#A78BFA" />
        <Btn icon={Instagram} label="Instagram" onClick={instagram} testId="invite-instagram-button" color="#F472B6" />
        <Btn icon={Mail} label="Email" href={`mailto:?subject=${encodeURIComponent("My bucket list")}&body=${enc("email")}`} testId="invite-email-button" color="#FCD34D" />
        <Btn icon={MessageSquare} label="SMS" href={`sms:?&body=${enc("sms")}`} testId="invite-sms-button" color="#7DD3FC" />
        <Btn icon={Copy} label="Copy text" onClick={copy} testId="invite-copy-text-button" color="#E2E8F0" />
        {typeof navigator !== "undefined" && navigator.share && <Btn icon={Share2} label="More…" onClick={share} testId="invite-native-share-button" color="#F59E0B" />}
      </div>
    </div>
  );
};
